// ============================================================================
// EcgStudiesPanel.tsx — ОЧЕРЕДЬ ИССЛЕДОВАНИЙ ЭКГ (экран врача).
//
// Список доступных пользователю исследований (права объекта + OwnScope решает
// сервер): дата, пациент, статус, ЧСС, качество, наличие заключения. Фильтры
// (статус/качество/заключение/период), ПОИСК по пациенту, названию и
// заключению, сортировка. Клик по строке — карточка исследования; клик по
// пациенту — его история (EcgPatientHistoryDialog, открывает страница).
//
// SSoT данных — сервер; здесь только чтение одним запросом и чистые отборы
// (lib/ecgStudyFilters). Ошибки и пустые состояния явные, без «вечной
// загрузки»: loading снимается в finally, ошибка показывает «Повторить».
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, RefreshCw, RotateCcw, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import {
    ECG_QUALITY_LABELS,
    ECG_STATUS_LABELS,
    listEcgStudies,
    type EcgQualityVerdict,
    type EcgStudyListItem,
} from '@/lib/ecgStudies';
import {
    ECG_STUDY_SORT_OPTIONS,
    EMPTY_ECG_STUDY_FILTERS,
    filterEcgStudies,
    sortEcgStudies,
    type EcgStudyFilterState,
    type EcgStudySort,
} from '@/lib/ecgStudyFilters';
import { patientOfStudy, type EcgPatient } from '@/lib/ecgPatients';
import { EcgStatusBadge } from '@/components/ecg/EcgStatusBadge';

interface Props {
    /** Меняется после сохранения разбора/заключения — очередь перечитывается. */
    refreshToken: number;
    /** Переход к пациенту: история его исследований. */
    onOpenPatient: (patient: EcgPatient) => void;
    onOpenStudy: (studyId: string) => void;
}

function hrLabel(item: EcgStudyListItem): string {
    const hr = item.measurements.hrBpm;
    return hr === null ? '—' : String(Math.round(hr));
}

function createdAtLabel(item: EcgStudyListItem): string {
    return formatValue(item.createdAt, 'date:dd.MM.yyyy HH:mm', 'timestamp') || item.title || '—';
}

function qualityFragment(quality: string): string {
    return quality.length > 60 ? `${quality.slice(0, 60)}…` : quality;
}

function conclusionTone(hasConclusion: boolean): string {
    return hasConclusion
        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700'
        : 'border-amber-500/50 bg-amber-500/10 text-amber-700';
}

const FILTER_GRID = 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-2 items-end';
const INPUT_CLASS = 'mt-0.5 block w-full h-8 rounded-md border bg-background px-2 text-xs';

export function EcgStudiesPanel({ refreshToken, onOpenPatient, onOpenStudy }: Props) {
    const [items, setItems] = useState<EcgStudyListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadedOnce, setLoadedOnce] = useState(false);
    const [filters, setFilters] = useState<EcgStudyFilterState>(EMPTY_ECG_STUDY_FILTERS);
    const [sort, setSort] = useState<EcgStudySort>('date_desc');

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const loaded = await listEcgStudies();
            setItems(loaded);
            setLoadedOnce(true);
        } catch (e) {
            setError(friendlyError(e));
            logErrorToJournal(e, 'ЭКГ: очередь исследований');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load, refreshToken]);

    const setFilter = <K extends keyof EcgStudyFilterState>(key: K, value: EcgStudyFilterState[K]) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const visibleItems = useMemo(() => {
        const filtered = filterEcgStudies(items, filters);
        return sortEcgStudies(filtered, sort);
    }, [items, filters, sort]);

    const awaitingConclusion = useMemo(
        () => items.filter((item) => !item.conclusion.trim()).length,
        [items],
    );

    const rangeInvalid = !!filters.dateFrom && !!filters.dateTo && filters.dateFrom > filters.dateTo;
    const filtersActive = !!(
        filters.search
        || filters.conclusion
        || filters.dateFrom
        || filters.dateTo
        || filters.status
        || filters.quality
    );
    const canReset = filtersActive || sort !== 'date_desc';

    const resetFilters = () => {
        setFilters(EMPTY_ECG_STUDY_FILTERS);
        setSort('date_desc');
    };

    return (
        <section className="border rounded-lg bg-card flex flex-col min-h-0 h-full">
            <div className="flex items-center gap-2 px-3 py-2 border-b flex-wrap">
                <span className="text-sm font-medium">Очередь исследований</span>
                {loadedOnce && (
                    <span className="text-[11px] text-muted-foreground">
                        {items.length === 0
                            ? 'нет исследований'
                            : `доступно ${items.length} · без заключения ${awaitingConclusion}`}
                    </span>
                )}
                {items.length > 0 && (
                    <span className="text-[11px] text-muted-foreground">
                        · показано {visibleItems.length}
                    </span>
                )}
                <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[11px] ml-auto"
                    onClick={() => void load()}
                    disabled={loading}
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                    Обновить
                </Button>
            </div>

            <div className="px-3 py-2 border-b space-y-2">
                <div className="relative">
                    <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="search"
                        value={filters.search}
                        onChange={(event) => setFilter('search', event.target.value)}
                        placeholder="Поиск: пациент, исследование, заключение…"
                        className="h-8 w-full rounded-md border bg-background pl-7 pr-7 text-xs"
                        aria-label="Поиск по очереди исследований"
                    />
                    {filters.search && (
                        <button
                            type="button"
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setFilter('search', '')}
                            aria-label="Очистить поиск"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>

                <div className={FILTER_GRID}>
                    <label className="text-[11px] text-muted-foreground">
                        Статус
                        <select
                            value={filters.status}
                            onChange={(event) => setFilter('status', event.target.value)}
                            className={INPUT_CLASS}
                        >
                            <option value="">Все статусы</option>
                            {Object.entries(ECG_STATUS_LABELS).map(([code, label]) => (
                                <option key={code} value={code}>{label}</option>
                            ))}
                        </select>
                    </label>
                    <label className="text-[11px] text-muted-foreground">
                        Качество
                        <select
                            value={filters.quality}
                            onChange={(event) => setFilter('quality', event.target.value as '' | EcgQualityVerdict)}
                            className={INPUT_CLASS}
                        >
                            <option value="">Любое качество</option>
                            {Object.entries(ECG_QUALITY_LABELS).map(([code, label]) => (
                                <option key={code} value={code}>{label}</option>
                            ))}
                        </select>
                    </label>
                    <label className="text-[11px] text-muted-foreground">
                        Заключение
                        <select
                            value={filters.conclusion}
                            onChange={(event) => setFilter('conclusion', event.target.value as '' | 'yes' | 'no')}
                            className={INPUT_CLASS}
                        >
                            <option value="">Любое</option>
                            <option value="no">Ожидает заключения</option>
                            <option value="yes">С заключением</option>
                        </select>
                    </label>
                    <label className="text-[11px] text-muted-foreground">
                        Дата с
                        <input
                            type="date"
                            value={filters.dateFrom}
                            onChange={(event) => setFilter('dateFrom', event.target.value)}
                            className={INPUT_CLASS}
                        />
                    </label>
                    <label className="text-[11px] text-muted-foreground">
                        Дата по
                        <input
                            type="date"
                            value={filters.dateTo}
                            onChange={(event) => setFilter('dateTo', event.target.value)}
                            className={INPUT_CLASS}
                        />
                    </label>
                    <label className="text-[11px] text-muted-foreground">
                        Сортировка
                        <select
                            value={sort}
                            onChange={(event) => setSort(event.target.value as EcgStudySort)}
                            className={INPUT_CLASS}
                        >
                            {ECG_STUDY_SORT_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </label>
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2 text-[11px] justify-start"
                        onClick={resetFilters}
                        disabled={!canReset}
                    >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Сбросить
                    </Button>
                </div>

                {rangeInvalid && (
                    <div className="text-[11px] text-amber-700 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1">
                        Дата «с» позже даты «по» — измените период.
                    </div>
                )}
            </div>

            <div className="p-3 flex-1 min-h-0 overflow-auto">
                {error && (
                    <div className="text-xs text-destructive border border-destructive/40 rounded-md px-2 py-1.5 flex items-center gap-2">
                        <span className="flex-1">{error}</span>
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => void load()}>
                            Повторить
                        </Button>
                    </div>
                )}

                {!error && loading && !loadedOnce && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                        Загружаю исследования…
                    </div>
                )}

                {!error && loadedOnce && items.length === 0 && (
                    <div className="text-sm text-muted-foreground">
                        Исследований пока нет. Сделайте разбор во вкладке «Новый разбор» —
                        запись появится в очереди.
                    </div>
                )}

                {!error && items.length > 0 && visibleItems.length === 0 && (
                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                        <span className="flex-1">По выбранным условиям исследований нет.</span>
                        {canReset && (
                            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={resetFilters}>
                                Сбросить фильтры
                            </Button>
                        )}
                    </div>
                )}

                {!error && visibleItems.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                    <th className="px-2 py-1.5 font-medium">Дата</th>
                                    <th className="px-2 py-1.5 font-medium">Пациент</th>
                                    <th className="px-2 py-1.5 font-medium">Исследование</th>
                                    <th className="px-2 py-1.5 font-medium">Статус</th>
                                    <th className="px-2 py-1.5 font-medium">ЧСС</th>
                                    <th className="px-2 py-1.5 font-medium">Качество</th>
                                    <th className="px-2 py-1.5 font-medium">Заключение</th>
                                    <th className="px-2 py-1.5 font-medium text-right"> </th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleItems.map((item) => {
                                    const patient = patientOfStudy(item);
                                    const patientLabel = patient?.name || 'не указан';
                                    const hasConclusion = !!item.conclusion.trim();
                                    const open = () => onOpenStudy(item.id);
                                    return (
                                        <tr
                                            key={item.id}
                                            className="border-t hover:bg-accent/30 cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                                            tabIndex={0}
                                            onClick={open}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    open();
                                                }
                                            }}
                                            aria-label={`Открыть исследование ${createdAtLabel(item)}`}
                                        >
                                            <td className="px-2 py-1.5 whitespace-nowrap">{createdAtLabel(item)}</td>
                                            <td className="px-2 py-1.5 max-w-[220px]">
                                                {patient ? (
                                                    <button
                                                        type="button"
                                                        className="text-left truncate underline-offset-2 hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                                                        title={patientLabel}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            onOpenPatient(patient);
                                                        }}
                                                    >
                                                        {patientLabel}
                                                    </button>
                                                ) : (
                                                    <span className="text-muted-foreground">не указан</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-1.5 max-w-[220px] truncate" title={item.title}>
                                                {item.title || '—'}
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <EcgStatusBadge code={item.statusCode} label={item.statusLabel} />
                                            </td>
                                            <td className="px-2 py-1.5 whitespace-nowrap">{hrLabel(item)}</td>
                                            <td className="px-2 py-1.5 max-w-[220px]" title={item.quality}>
                                                <div className="text-xs">{ECG_QUALITY_LABELS[item.qualityVerdict]}</div>
                                                <div className="text-[11px] text-muted-foreground truncate">
                                                    {qualityFragment(item.quality) || '—'}
                                                </div>
                                            </td>
                                            <td className="px-2 py-1.5">
                                                <span className={`inline-block text-[11px] border rounded px-1.5 py-0.5 ${conclusionTone(hasConclusion)}`}>
                                                    {hasConclusion ? 'Есть' : 'Ожидает'}
                                                </span>
                                            </td>
                                            <td className="px-2 py-1.5 text-right">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-6 px-2 text-[11px]"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        open();
                                                    }}
                                                >
                                                    Открыть
                                                    <ChevronRight className="h-3.5 w-3.5" />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {loading && loadedOnce && !error && (
                <div className="px-3 py-1.5 border-t text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    Обновляю очередь…
                </div>
            )}

            <div className="px-3 py-1.5 border-t text-[11px] text-muted-foreground">
                Показаны исследования, доступные вашей роли. Пациент — владелец персональной записи;
                при наличии карточки пациента связь берётся из реквизита. Клик по пациенту открывает его историю.
            </div>
        </section>
    );
}
